'use client';

import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { Loader2 } from 'lucide-react';
import { UserProfileView } from '@/components/profile/UserProfileView';
import { BusinessProfileView } from '@/components/profile/BusinessProfileView';
import { UserBusinessProfileView } from '@/components/profile/UserBusinessProfileView';
import { AccountHeader } from '@/components/layouts/AccountHeader';
import { useUser } from '@/hooks/use-user';
import { useCommunity } from '@/hooks/use-community';
import { communityIdFromPathname } from '@/lib/community-route';
import axiosInstance from '@/lib/axios';
import type { PublicUserWithCommunities } from '@/types/profile';
import type { UserWithCommunities } from '@/types';

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

export default function ProfilePage() {
  const params = useParams<{ userId?: string; businessId?: string; communityId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser, isLoading: isUserLoading } = useUser();

  const userId = params.userId;
  const businessId = params.businessId;
  const communitySegment = params.communityId ?? communityIdFromPathname(location.pathname);

  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const isPrivateProfile = normalizedPath === '/profile' || /\/profile$/.test(normalizedPath);

  const { community: communityResolution, isLoading: isCommunityScopeLoading } =
    useCommunity(communitySegment);

  // Determine profile type from URL
  const isPublicUser =
    location.pathname.startsWith('/u/') || (communitySegment && location.pathname.includes('/u/'));
  const isPublicBusiness =
    location.pathname.startsWith('/b/') || (communitySegment && location.pathname.includes('/b/'));
  const isPublicUserBusiness =
    location.pathname.startsWith('/ub/') ||
    (communitySegment && location.pathname.includes('/ub/'));

  const needsCanonicalCommunityForPrivateScope = isPrivateProfile && Boolean(communitySegment);
  const awaitingCommunityCanonicalScope =
    needsCanonicalCommunityForPrivateScope &&
    isCommunityScopeLoading &&
    !communityResolution?.community;
  // Fetch data based on type
  const { data: userData, isLoading: userDataLoading } = useSWR(
    isPublicUser && userId ? `/api/user/public/${userId}` : null,
    fetcher
  );

  const { data: businessData, isLoading: businessDataLoading } = useSWR(
    isPublicBusiness && businessId ? `/api/public/business/${businessId}` : null,
    fetcher
  );

  const { data: userBusinessData, isLoading: userBusinessDataLoading } = useSWR(
    isPublicUserBusiness && userId && businessId
      ? `/api/user/public/business/${userId}/${businessId}`
      : null,
    fetcher
  );

  const isLoading =
    isUserLoading ||
    userDataLoading ||
    businessDataLoading ||
    userBusinessDataLoading ||
    awaitingCommunityCanonicalScope;
  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary opacity-50" />
        <p className="text-sm font-medium text-muted-foreground animate-pulse">
          Loading profile...
        </p>
      </div>
    );
  }

  // Handle Private Profile
  if (isPrivateProfile) {
    if (!currentUser)
      return <div className="p-8 text-center">Please login to view your profile</div>;

    const editPath = communitySegment ? `/${communitySegment}/profile/edit` : '/profile/edit';
    const onboardingScopeCommunityId = communityResolution?.community?.id;
    const profileView = (
      <UserProfileView
        user={currentUser as unknown as PublicUserWithCommunities}
        isEditable
        onEdit={() => navigate(editPath)}
        onboardingScopeCommunityId={communitySegment ? onboardingScopeCommunityId : undefined}
      />
    );

    // Wrap with AccountHeader only on global (non-community) routes
    if (!communitySegment) {
      return (
        <div className="min-h-screen bg-background flex flex-col">
          <AccountHeader user={currentUser as unknown as UserWithCommunities} />
          <main className="flex-1 p-2 md:p-8 overflow-y-auto w-full">{profileView}</main>
        </div>
      );
    }

    return profileView;
  }

  // Handle Public User Profile
  if (isPublicUser) {
    if (!userData?.data)
      return <div className="p-20 text-center text-muted-foreground">User not found</div>;
    const inner = <UserProfileView user={userData.data} />;
    if (!communitySegment) {
      return (
        <div className="min-h-screen bg-background flex flex-col">
          <AccountHeader user={currentUser ?? undefined} />
          <main className="flex-1 p-2 md:p-8 overflow-y-auto w-full">{inner}</main>
        </div>
      );
    }
    return inner;
  }

  // Handle Public Business Profile
  if (isPublicBusiness) {
    if (!businessData?.data)
      return <div className="p-20 text-center text-muted-foreground">Business not found</div>;
    const business = businessData.data;
    const isLinkedMember =
      !!currentUser && !!business.users?.some((u: { id: string }) => u.id === currentUser.id);
    const inner = <BusinessProfileView business={business} isEditable={isLinkedMember} />;
    if (!communitySegment) {
      return (
        <div className="min-h-screen bg-background flex flex-col">
          <AccountHeader user={currentUser ?? undefined} />
          <main className="flex-1 p-2 md:p-8 overflow-y-auto w-full">{inner}</main>
        </div>
      );
    }
    return inner;
  }

  // Handle Public User-Business Profile
  if (isPublicUserBusiness) {
    if (!userBusinessData?.data)
      return <div className="p-20 text-center text-muted-foreground">Profile not found</div>;
    const userBusiness = userBusinessData.data;
    const isOwnPair =
      !!currentUser &&
      (currentUser.id === userBusiness.userId || currentUser.id === userBusiness.user?.id);
    const inner = (
      <UserBusinessProfileView
        userBusiness={userBusiness}
        isEditable={isOwnPair}
        onEdit={
          isOwnPair
            ? () =>
                navigate(
                  communitySegment
                    ? `/${communitySegment}/create/business/${businessId}`
                    : `/create/business/${businessId}`,
                  { state: { initialData: userBusiness.business } }
                )
            : undefined
        }
      />
    );
    if (!communitySegment) {
      return (
        <div className="min-h-screen bg-background flex flex-col">
          <AccountHeader user={currentUser ?? undefined} />
          <main className="flex-1 p-2 md:p-8 overflow-y-auto w-full">{inner}</main>
        </div>
      );
    }
    return inner;
  }

  return <div className="p-20 text-center text-muted-foreground">Invalid profile route</div>;
}
